import {
  Dealer,
  TaxBreakdown,
  TaxLevel,
} from '@upstart/harmony/lib/legos/dealerTypes';
import { Pencil } from '@upstart/harmony/lib/legos/pencilTypes';
import ChangeLog from '@upstart/harmony/lib/pencil-changelog';
import React, { useCallback, useEffect, useState } from 'react';
import { cloneDeep, get, isEmpty, set, startCase } from 'lodash';
import { Alert } from 'react-bootstrap';
import Modal from 'react-modal';
import { FinanceType, TaxType } from '@upstart/harmony/lib/legos/constants';
import { Action, ModalType } from 'util/amplitudeEvents';
import {
  getDealerConfigForTaxByDealerAddress,
  isMarketScanPaymentsEnabled,
  isThirdPartyVendorTaxesEnabled,
} from '@upstart/harmony/lib/dealer-utils';
import { isDefined } from '@upstart/harmony/lib/legos/utils/tsutils';
import {
  MarketScanColoradoTaxConfig,
  MarketScanIllinoisTaxConfig,
  MarketScanMissouriTaxConfig,
} from '@upstart/harmony/lib/legos/marketScanConfig';
import {
  getDefaultTaxTypeLabel,
  hasTaxAmountOverride,
} from '../../../util/pencilUtils';
import PencilService from '../../../services/PencilService';
import Input, {
  CachedInput,
  formatToPrecision,
  PercentageInput,
} from '../../PencilEditor/Input';
import { scrollableModalStyles } from '../../../util/constants';
import DataTable from '../../DataTable';
import LoadingOverlay from '../../LoadingOverlay';
import './OfferTaxModal.scss';
import ColoradoTaxConfigs from './ColoradoTaxConfigs';
import MissouriTaxConfigs from './MissouriTaxConfigs';
import IllinoisTaxConfigs from './IllinoisTaxConfigs';

type AddressInfo = {
  dealerAddressData: {
    county: string;
    city: string;
    state: string;
  };
  customerAddressData: {
    county?: string;
    city?: string;
    state?: string;
  };
};

const REGION_ID_NOT_FOUND_ERROR =
  'RegionId could not be derived by data provided in the Request';

const AGGREGATED_USE_RATE_LABEL = 'Aggregated Use Tax Rate';
const WARRANTY_LABEL = 'Warranty Tax Rate:';

// Placeholder for aggregated use rate, currently set as 9% as we support IL, Chicago only
const AGGREGATED_USE_RATE_PLACEHOLDER = '9';

const initializeTaxFields = (currentTaxBreakdown: TaxBreakdown) => {
  const copyTaxBreakdown = cloneDeep(currentTaxBreakdown);
  // fill in default tax names if they aren't set
  Object.values(TaxType).forEach(taxType => {
    if (
      get(copyTaxBreakdown, [taxType, 'rate']) &&
      !get(copyTaxBreakdown, [taxType, 'name'])
    ) {
      set(copyTaxBreakdown, [taxType, 'name'], getDefaultTaxTypeLabel(taxType));
    }
  });

  if (typeof copyTaxBreakdown.taxByDealerAddress === 'object') {
    copyTaxBreakdown.taxByDealerAddress = undefined;
  }
  return copyTaxBreakdown;
};

/**
 *
 * @param dealer
 * @param state
 */
const shouldDisplayMissouriTaxConfigs = (
  dealer: Dealer,
  state: string | undefined,
) => {
  const stateLowerCase = state?.toLowerCase();
  return (
    isMarketScanPaymentsEnabled(dealer) &&
    (stateLowerCase === 'mo' || stateLowerCase === 'missouri')
  );
};

/**
 *
 * @param dealer
 * @param financeType
 * @param state
 */
const shouldDisplayColoradoTaxConfigs = (
  dealer: Dealer,
  financeType: FinanceType,
  state: string | undefined,
) => {
  const stateLowerCase = state?.toLowerCase();
  return (
    isMarketScanPaymentsEnabled(dealer) &&
    financeType === FinanceType.LEASE &&
    (stateLowerCase === 'co' || stateLowerCase === 'colorado')
  );
};

/**
 *
 * @param dealer
 * @param financeType
 * @param state
 */
const shouldDisplayIllinoisTaxConfigs = (
  dealer: Dealer,
  financeType: FinanceType,
  state: string | undefined,
) => {
  const stateLowerCase = state?.toLowerCase();
  return (
    isMarketScanPaymentsEnabled(dealer) &&
    financeType === FinanceType.LEASE &&
    (stateLowerCase === 'il' || stateLowerCase === 'illinois')
  );
};

/**
 *
 * @param dealer
 * @param financeType
 * @param state
 */
const shouldDisplayAggregatedUseTaxRate = (
  dealer: Dealer,
  financeType: FinanceType,
  state: string | undefined,
) => {
  const stateLowerCase = state?.toLowerCase();
  return (
    isMarketScanPaymentsEnabled(dealer) &&
    financeType === FinanceType.LEASE &&
    (stateLowerCase === 'il' || stateLowerCase === 'illinois')
  );
};

function TaxModalContent({
  financeType,
  taxBreakdown,
  onChange,
  updateTaxes,
  handleClose,
  activeOfferIndex,
  changeLog,
  pencil,
  dealId,
  addressInfo,
  dealer,
  sendAmplitudeEvent,
  disabled = false,
}: {
  financeType: FinanceType;
  taxBreakdown: TaxBreakdown;
  onChange: (breakdown: TaxBreakdown) => void;
  updateTaxes?: (
    newValue: number,
    taxType: string,
    taxName: string,
    prop: string,
  ) => void;
  handleClose: () => void;
  activeOfferIndex: number;
  changeLog?: ChangeLog;
  pencil: Pencil;
  dealId: string;
  addressInfo: AddressInfo;
  dealer: Dealer;
  sendAmplitudeEvent?: (event: ModalType, action: Action) => void;
  disabled?: boolean;
}) {
  const [useAmountOverride, setUseAmountOverride] = useState(false);

  const [editTaxBreakdown, setEditTaxBreakdown] = useState(() =>
    initializeTaxFields(taxBreakdown),
  );

  const [isLoadingTaxBreakdown, setIsLoadingTaxBreakdown] = useState(false);
  // derive editTaxBreakdown from the provided taxBreakdown prop

  useEffect(() => {
    initializeTaxFields(taxBreakdown);
    setUseAmountOverride(hasTaxAmountOverride(taxBreakdown));
  }, [taxBreakdown]);

  const hasTaxesRatesReturnedByThirdPartyVendor = isThirdPartyVendorTaxesEnabled(
    dealer,
  );

  const useWarrantyTaxRate =
    FinanceType.LEASE === financeType
      ? !!dealer.featureConfig?.useWarrantyTaxForLease
      : !!dealer.featureConfig?.useWarrantyTaxForPurchaseAndCash;

  const dealerConfigForTaxByDealerAddress = getDealerConfigForTaxByDealerAddress(
    dealer,
  );

  const taxByDealerAddress =
    dealerConfigForTaxByDealerAddress ||
    !hasTaxesRatesReturnedByThirdPartyVendor ||
    isEmpty(addressInfo.customerAddressData) ||
    taxBreakdown.taxByDealerAddress;

  const [addressData, setAddressData] = useState(
    taxByDealerAddress
      ? addressInfo.dealerAddressData
      : addressInfo.customerAddressData,
  );

  /**
   * when MS Payments flag is enabled then we should display the aggregated use rate field for lease deals
   * with customer address or dealer address in IL
   */
  const displayAggregatedUseTaxRate = shouldDisplayAggregatedUseTaxRate(
    dealer,
    financeType,
    addressData.state,
  );

  const displayColoradoTaxConfigs = shouldDisplayColoradoTaxConfigs(
    dealer,
    financeType,
    addressData.state,
  );

  const displayMissouriTaxConfigs = shouldDisplayMissouriTaxConfigs(
    dealer,
    addressData.state,
  );

  const displayIllinoisTaxConfigs = shouldDisplayIllinoisTaxConfigs(
    dealer,
    financeType,
    addressData.state,
  );

  const shouldDisplayItemizedUseRate =
    financeType === FinanceType.LEASE && !isMarketScanPaymentsEnabled(dealer);

  const [isTaxByDealerAddress, setIsTaxByDealerAddress] = useState(
    taxByDealerAddress,
  );

  const areTaxesDisabled = !!dealer?.featureConfig?.disableTaxes;

  const getDefaultTaxRates = useCallback(
    // eslint-disable-next-line no-shadow
    async (isForceTaxByDealerForTaxCalculation, financeTypeForTaxRates) =>
      PencilService.getDefaultTaxRates({
        dealId,
        isForceTaxByDealerForTaxCalculation,
        financeType: financeTypeForTaxRates,
      }),
    [dealId],
  );

  const [hasCustomerTaxError, setHasCustomerTaxError] = useState(
    isEmpty(addressInfo.customerAddressData),
  );
  const [hasCustomerAddressError, setHasCustomerAddressError] = useState(false);

  const handleChangeTaxByDealer = useCallback(
    async (shouldTaxByDealerAddress, preloadedData) => {
      setIsTaxByDealerAddress(shouldTaxByDealerAddress);
      setAddressData(
        shouldTaxByDealerAddress
          ? addressInfo.dealerAddressData
          : addressInfo.customerAddressData,
      );
      setIsLoadingTaxBreakdown(true);
      let data;

      try {
        if (!preloadedData) {
          data = await getDefaultTaxRates(
            shouldTaxByDealerAddress,
            financeType,
          );
        } else {
          data = preloadedData;
        }
        const returnedTaxBreakdown = data?.taxBreakdown;

        if (returnedTaxBreakdown) {
          setEditTaxBreakdown({
            ...returnedTaxBreakdown,
            taxByDealerAddress: shouldTaxByDealerAddress,
          });
        }
      } catch (ex) {
        if (!shouldTaxByDealerAddress) {
          setHasCustomerTaxError(true);
        }
      } finally {
        setIsLoadingTaxBreakdown(false);
      }
    },
    [addressInfo, getDefaultTaxRates],
  );

  const [customerDefaultTaxData, setCustomerDefaultTaxData] = useState();
  const [dealerDefaultTaxData, setDealerDefaultTaxData] = useState();

  const handleColoradoTaxConfigurationChange = (
    property: keyof MarketScanColoradoTaxConfig,
    newValue: boolean,
  ) => {
    const newTaxBreakdown = cloneDeep(editTaxBreakdown);
    if (!newTaxBreakdown.taxConfigurations) {
      newTaxBreakdown.taxConfigurations = {};
    }

    // initialize tax configurations for CO
    if (!newTaxBreakdown.taxConfigurations.CO) {
      newTaxBreakdown.taxConfigurations.CO = {
        collectCityTaxInPayment: false,
        collectCountyTaxInPayment: false,
      };
    }
    newTaxBreakdown.taxConfigurations.CO[property] = newValue;

    setEditTaxBreakdown(newTaxBreakdown);
  };

  const handleMissouriTaxConfigurationChange = (
    property: keyof MarketScanMissouriTaxConfig,
    newValue: boolean,
  ) => {
    const newTaxBreakdown = cloneDeep(editTaxBreakdown);
    if (!newTaxBreakdown.taxConfigurations) {
      newTaxBreakdown.taxConfigurations = {};
    }

    // initialize tax configurations for MO
    if (!newTaxBreakdown.taxConfigurations.MO) {
      newTaxBreakdown.taxConfigurations.MO = {
        noSalesTax: false,
      };
    }
    newTaxBreakdown.taxConfigurations.MO[property] = newValue;

    setEditTaxBreakdown(newTaxBreakdown);
  };

  const handleIllinoisTaxConfigurationChange = (
    property: keyof MarketScanIllinoisTaxConfig,
    newValue: boolean,
  ) => {
    const newTaxBreakdown = cloneDeep(editTaxBreakdown);
    if (!newTaxBreakdown.taxConfigurations) {
      newTaxBreakdown.taxConfigurations = {};
    }

    // initialize tax configurations for IL
    if (!newTaxBreakdown.taxConfigurations.IL) {
      newTaxBreakdown.taxConfigurations.IL = {
        noMonthlyUseTax: false,
        taxCappedInterestOnSalesTax: false,
      };
    }
    newTaxBreakdown.taxConfigurations.IL[property] = newValue;

    setEditTaxBreakdown(newTaxBreakdown);
  };

  useEffect(() => {
    const checkForCustomerRates = async () => {
      // Check to see if customer tax rate available via atc. If error, ensure we use dealer based rates.
      if (!dealerDefaultTaxData) {
        const dealerRes = await getDefaultTaxRates(true, financeType);
        setDealerDefaultTaxData(dealerRes);
      }

      try {
        if (!customerDefaultTaxData) {
          const customerRes = await getDefaultTaxRates(false, financeType);
          setCustomerDefaultTaxData(customerRes);
          setHasCustomerAddressError(false);
          setHasCustomerTaxError(false);
        }
      } catch (e) {
        // if error is that ATC Region cannot be found, show disclaimer saying customer address isn't an option
        if ((e as any)?.error?.includes(REGION_ID_NOT_FOUND_ERROR)) {
          setHasCustomerAddressError(true);
        }

        // If we don't have this data
        setHasCustomerTaxError(true);
        setIsLoadingTaxBreakdown(false);
        await handleChangeTaxByDealer(true, dealerDefaultTaxData);
      }
    };

    if (!isEmpty(addressInfo.customerAddressData) && !hasCustomerTaxError) {
      // If we have customer address and don't currently have an error, set error related state data.
      checkForCustomerRates();
    }
  }, [
    addressInfo,
    getDefaultTaxRates,
    handleChangeTaxByDealer,
    hasCustomerTaxError,
    customerDefaultTaxData,
    dealerDefaultTaxData,
  ]);

  /**
   * Change a prop, like name or rate, for an entry in editTaxBreakdown
   */
  const changeTaxBreakdownProperty = (
    source: TaxType,
    prop: any,
    value: any,
    taxName: any,
  ) => {
    value = value === '0' ? '' : value;
    const name = taxName ?? startCase(source);
    if (updateTaxes) {
      updateTaxes(value, source, name, prop);
    }
    if (changeLog) {
      changeLog.updateTaxes(
        financeType,
        pencil,
        activeOfferIndex,
        value,
        source,
        name,
        prop,
      );
    }
    return setEditTaxBreakdown({
      ...editTaxBreakdown,
      [source]: {
        ...(editTaxBreakdown || {})[source],
        [prop]: value,
      },
    });
  };

  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const save = (event: any) => {
    event.preventDefault();

    const saveTaxBreakdown = cloneDeep(editTaxBreakdown);
    const newValidationErrors: string[] = [];
    if (displayAggregatedUseTaxRate) {
      if (!isDefined(saveTaxBreakdown.aggregatedUseTaxRate)) {
        newValidationErrors.push(
          'Aggregated Use Tax Rate is required. Enter zero if not applicable.',
        );
      }
    }
    Object.entries(saveTaxBreakdown).forEach(([k, item]) => {
      // validate values and remove entries with undefined rates
      if (
        k === 'taxByDealerAddress' ||
        k === 'aggregatedUseTaxRate' ||
        k === 'taxConfigurations' ||
        k === 'warranty'
      ) {
        return;
      }
      const key = k as TaxType;
      const entry = item as TaxLevel;
      const breakdown = saveTaxBreakdown?.[key];

      if (!entry.rate) {
        // save a rate as a placeholder if useAmountOverride is set
        if (breakdown && (useAmountOverride || entry.useRate)) {
          breakdown.rate = 0;
        } else {
          delete saveTaxBreakdown[key];
        }
        return;
      }

      // if a tax entry has a rate, it should have a name
      if (!entry.name) {
        saveTaxBreakdown[key]!.name = getDefaultTaxTypeLabel(key);
      }

      // normalize values for amountOverride based on whether it's enabled
      if (useAmountOverride && breakdown) {
        breakdown.amountOverride = breakdown.amountOverride || 0;
      } else {
        delete saveTaxBreakdown?.[key]?.amountOverride;
      }
    });
    setValidationErrors(newValidationErrors);

    // continue saving if there were no errors
    if (newValidationErrors.length === 0) {
      onChange(saveTaxBreakdown);
      if (sendAmplitudeEvent) {
        sendAmplitudeEvent(ModalType.TAX_RATE, Action.FINISH);
      }
      if (useAmountOverride && sendAmplitudeEvent) {
        sendAmplitudeEvent(ModalType.TAX_RATE, Action.OVERRIDE);
      }
      handleClose();
    }
  };

  const rows = Object.values(TaxType)
    .map(taxType => {
      const taxRate = get(editTaxBreakdown, [taxType, 'rate']);
      const useRate = get(editTaxBreakdown, [taxType, 'useRate']);
      const taxName = get(editTaxBreakdown, [taxType, 'name']);
      const maxAmount = get(editTaxBreakdown, [taxType, 'maxAmount']);
      const amountOverride = get(editTaxBreakdown, [taxType, 'amountOverride']);

      // It was not detected customer has a county tax, so don't include as a row
      if (taxType === TaxType.CUSTOMER_COUNTY && !taxRate) {
        return undefined;
      }

      return [
        <Input
          key="name"
          className={
            taxType === TaxType.CUSTOMER_COUNTY ? 'disabled-input' : undefined
          }
          placeholder={getDefaultTaxTypeLabel(taxType)}
          value={taxName}
          onChange={(value: string) =>
            changeTaxBreakdownProperty(taxType, 'name', value, taxName)
          }
          disabled={disabled || taxType === TaxType.CUSTOMER_COUNTY}
        />,
        !useAmountOverride && (
          <PercentageInput
            key="rate"
            className={
              !isTaxByDealerAddress || taxType === TaxType.CUSTOMER_COUNTY
                ? 'disabled-input'
                : undefined
            }
            value={taxRate}
            onChange={value =>
              changeTaxBreakdownProperty(taxType, 'rate', value, taxName)
            }
            placeholder={'0'}
            disabled={
              disabled ||
              !isTaxByDealerAddress ||
              taxType === TaxType.CUSTOMER_COUNTY
            }
          />
        ),
        useAmountOverride && (
          <div className="finance-input">
            $
            <CachedInput
              key="amountOverride"
              className={
                taxType === TaxType.CUSTOMER_COUNTY
                  ? 'disabled-input'
                  : undefined
              }
              value={amountOverride}
              onChange={value =>
                changeTaxBreakdownProperty(
                  taxType,
                  'amountOverride',
                  value === '' ? undefined : parseFloat(value),
                  taxName,
                )
              }
              placeholder={'0'}
              disabled={disabled || taxType === TaxType.CUSTOMER_COUNTY}
            />
          </div>
        ),
        shouldDisplayItemizedUseRate && (
          <PercentageInput
            key="useRate"
            className={
              taxType === TaxType.CUSTOMER_COUNTY ? 'disabled-input' : undefined
            }
            value={useRate}
            onChange={value =>
              changeTaxBreakdownProperty(taxType, 'useRate', value, taxName)
            }
            placeholder={formatToPrecision((taxRate || 0) * 100).toString()}
            disabled={disabled || taxType === TaxType.CUSTOMER_COUNTY}
          />
        ),
        !useAmountOverride && !isMarketScanPaymentsEnabled(dealer) && (
          <div className="finance-input">
            $
            <CachedInput
              key="maxAmount"
              className={
                taxType === TaxType.CUSTOMER_COUNTY
                  ? 'disabled-input'
                  : undefined
              }
              value={maxAmount}
              onChange={value =>
                changeTaxBreakdownProperty(
                  taxType,
                  'maxAmount',
                  value === '' ? undefined : parseFloat(value),
                  taxName,
                )
              }
              placeholder={'0'}
              disabled={disabled || taxType === TaxType.CUSTOMER_COUNTY}
            />
          </div>
        ),
      ].filter(r => !!r);
    })
    .filter(r => !!r);

  return (
    <>
      <h2>Taxes</h2>
      {(validationErrors.length && (
        <Alert bsStyle="danger">
          {validationErrors.map(error => (
            <div key={error}>{error}</div>
          ))}
        </Alert>
      )) ||
        null}
      <DataTable
        className="tax-modal-table"
        rows={rows}
        headers={[
          'Name',
          !useAmountOverride && 'Rate',
          useAmountOverride && 'Amount',
          shouldDisplayItemizedUseRate && 'Use Rate',
          !useAmountOverride &&
            !isMarketScanPaymentsEnabled(dealer) &&
            'Maximum Tax Amount',
        ].filter(r => !!r)}
      />
      {displayAggregatedUseTaxRate && (
        <div className="aggregatedUseTaxRateContainer">
          <span className="aggregatedUseTaxRateLabel">
            {AGGREGATED_USE_RATE_LABEL}
          </span>
          <PercentageInput
            key="useRate"
            value={editTaxBreakdown.aggregatedUseTaxRate}
            onChange={value =>
              setEditTaxBreakdown({
                ...editTaxBreakdown,
                aggregatedUseTaxRate: value,
              })
            }
            placeholder={AGGREGATED_USE_RATE_PLACEHOLDER}
            required
          />
        </div>
      )}
      {useWarrantyTaxRate && 'warranty' in editTaxBreakdown && (
        <div className="aggregatedUseTaxRateContainer">
          <span className="aggregatedUseTaxRateLabel">{WARRANTY_LABEL}</span>
          <PercentageInput
            key="useWarranty"
            value={editTaxBreakdown.warranty}
            onChange={value =>
              setEditTaxBreakdown({
                ...editTaxBreakdown,
                warranty: value,
              })
            }
            placeholder={WARRANTY_LABEL}
            required
            // disabled
          />
        </div>
      )}
      {displayColoradoTaxConfigs && (
        <ColoradoTaxConfigs
          collectCityTaxInPayment={
            editTaxBreakdown.taxConfigurations?.CO?.collectCityTaxInPayment ||
            false
          }
          collectCountyTaxInPayment={
            editTaxBreakdown.taxConfigurations?.CO?.collectCountyTaxInPayment ||
            false
          }
          collectCityTaxInPaymentHandler={(value: boolean | undefined) => {
            handleColoradoTaxConfigurationChange(
              'collectCityTaxInPayment',
              value || false,
            );
          }}
          collectCountyTaxInPaymentHandler={(value: boolean | undefined) => {
            handleColoradoTaxConfigurationChange(
              'collectCountyTaxInPayment',
              value || false,
            );
          }}
        />
      )}
      {displayMissouriTaxConfigs && (
        <MissouriTaxConfigs
          noSalesTax={
            editTaxBreakdown.taxConfigurations?.MO?.noSalesTax || false
          }
          noSalesTaxHandler={(value: boolean | undefined) => {
            handleMissouriTaxConfigurationChange('noSalesTax', value || false);
          }}
        />
      )}
      {displayIllinoisTaxConfigs && (
        <IllinoisTaxConfigs
          noMonthlyUseTax={
            editTaxBreakdown.taxConfigurations?.IL?.noMonthlyUseTax || false
          }
          taxCappedInterestOnSalesTax={
            editTaxBreakdown.taxConfigurations?.IL
              ?.taxCappedInterestOnSalesTax || false
          }
          noMonthlyUseTaxHandler={(value: boolean | undefined) => {
            handleIllinoisTaxConfigurationChange(
              'noMonthlyUseTax',
              value || false,
            );
          }}
          taxCappedInterestOnSalesTaxHandler={(value: boolean | undefined) => {
            handleIllinoisTaxConfigurationChange(
              'taxCappedInterestOnSalesTax',
              value || false,
            );
          }}
        />
      )}
      {!useAmountOverride && !areTaxesDisabled && (
        <div>
          <p className="addressLabel">BASE TAXES ON:</p>
          <span className="toggle-button-group">
            {!hasCustomerTaxError &&
              !dealerConfigForTaxByDealerAddress &&
              hasTaxesRatesReturnedByThirdPartyVendor && (
                <button
                  className={
                    !isTaxByDealerAddress
                      ? 'selectedButton'
                      : 'unselectedButton'
                  }
                  onClick={e =>
                    handleChangeTaxByDealer(false, customerDefaultTaxData)
                  }
                >
                  {"Customer's Address"}
                </button>
              )}
            <button
              className={
                isTaxByDealerAddress ? 'selectedButton' : 'unselectedButton'
              }
              onClick={e =>
                hasCustomerTaxError
                  ? undefined
                  : handleChangeTaxByDealer(true, dealerDefaultTaxData)
              }
            >
              {"Dealer's Address"}
            </button>
          </span>

          <div className={'addressGrid'}>
            <div className={'addressLabel'}>State</div>
            <div className={'addressText'}>{addressData.state}</div>

            <div className={'addressLabel'}>County</div>
            <div className={'addressText'}>{addressData.county}</div>

            <div className={'addressLabel'}>City</div>
            <div className={'addressText'}>{addressData.city}</div>
          </div>
          <p className="disclaimerText">
            {hasCustomerAddressError ? (
              <span>
                * Customer Address contains invalid city/state/county
                combination.
              </span>
            ) : (
              <span>
                * Changing the location will reset any existing tax
                customization
              </span>
            )}
          </p>
        </div>
      )}
      <div className="modal-footer">
        {/* Market Scan only supports overriding tax amounts for Purchase and Cash, not Lease */}
        {isMarketScanPaymentsEnabled(dealer) &&
        financeType === FinanceType.LEASE ? null : (
          <div className="tax-modal-override-amount">
            <label className="pencil-input-label">
              <Input
                onChange={() => setUseAmountOverride(v => !v)}
                checked={useAmountOverride}
                type="checkbox"
                disabled={disabled}
              />
              Override Amounts
            </label>
            {useAmountOverride && (
              <span className="tax-modal-override-amount-disclaimer">
                Tax overrides will be applied on a per offer basis.
              </span>
            )}
          </div>
        )}
        <LoadingOverlay active={isLoadingTaxBreakdown} size="small">
          <button
            key="save"
            className="modal-button save cc-button-primary"
            onClick={save}
            disabled={disabled || isLoadingTaxBreakdown}
          >
            Save
          </button>
        </LoadingOverlay>
      </div>
    </>
  );
}

export default function OfferTaxModal({
  isOpen,
  handleClose,
  ...rest
}: {
  isOpen: boolean;
  handleClose: () => void;
  financeType: FinanceType;
  taxBreakdown: TaxBreakdown;
  onChange: (breakdown: TaxBreakdown) => void;
  updateTaxes?: (
    newValue: number,
    taxType: string,
    taxName: string,
    prop: string,
  ) => void;
  activeOfferIndex: number;
  changeLog?: ChangeLog;
  pencil: Pencil;
  dealId: string;
  addressInfo: AddressInfo;
  dealer: Dealer;
  sendAmplitudeEvent?: (event: ModalType, action: Action) => void;
  disabled: boolean;
}) {
  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={() => {
        if (rest.sendAmplitudeEvent) {
          rest.sendAmplitudeEvent(ModalType.TAX_RATE, Action.CANCEL);
        }
        handleClose();
      }}
      contentLabel="Tax Modal"
      style={scrollableModalStyles as any}
    >
      <span
        className="close"
        onClick={() => {
          if (rest.sendAmplitudeEvent) {
            rest.sendAmplitudeEvent(ModalType.TAX_RATE, Action.CANCEL);
          }
          handleClose();
        }}
      >
        X
      </span>
      {isOpen && <TaxModalContent handleClose={handleClose} {...rest} />}
    </Modal>
  );
}
